<!--
  THE activity row (E4 RF-36 to RF-43). Listing-table pattern: `table-hover table-centered`,
  `th.text-primary-emphasis`, the `btn-dropdown-list` dots menu with translated `mat-menu-item`
  entries. Every state carries an icon and text, never colour alone (RNF-08).
-->
@if (!activities?.length) {
  <p class="text-muted font-13 mb-0 py-2">
    <i class="mdi mdi-check-all me-1"></i>{{ emptyKey | translate }}
  </p>
} @else {
  <div class="table-responsive">
    <table class="table table-hover table-centered mb-0">
      <thead>
        <tr>
          <th class="text-primary-emphasis">{{ 'kanban.cardDetails.activityType' | translate }}</th>
          <th class="text-primary-emphasis">{{ 'kanban.cardDetails.activityWhen' | translate }}</th>
          <th class="text-primary-emphasis">{{ 'task.home.columnStatus' | translate }}</th>
          @if (showResponsible) {
            <th class="text-primary-emphasis">{{ 'kanban.cardDetails.activityOwner' | translate }}</th>
          }
          <th><span class="sr-only"></span></th>
        </tr>
      </thead>
      <tbody>
        @for (task of activities; track task._id) {
          <tr
            class="cursor-pointer"
            [class.table-active]="selectable && selectedId === task._id"
            [attr.aria-selected]="selectable ? selectedId === task._id : null"
            (click)="onRowClick(task)"
          >
            <td>
              <div class="d-flex align-items-start gap-2">
                <i class="mdi font-18 text-muted" [ngClass]="typeIcon(task)" [matTooltip]="typeLabel(task) | translate"></i>
                <div class="d-flex flex-column min-w-0">
                  <span class="fw-bold text-truncate">{{ title(task) }}</span>
                  <small class="text-muted">
                    {{ typeLabel(task) | translate }}
                    @if (showDeal && dealName(task)) {
                      · <i class="mdi mdi-tag-outline"></i> {{ dealName(task) }}
                    }
                    <!-- E4 validation N2: a deal the reader may not see — said, never shown; the actions are a supervisor's. -->
                    @if (isLocked(task)) {
                      ·
                      <span
                        class="badge campaign-badge is-warn"
                        [matTooltip]="'task.activity.dealHiddenHint' | translate"
                        [attr.aria-label]="'task.activity.dealHiddenHint' | translate"
                      >
                        <i class="mdi mdi-lock-outline me-1"></i>{{ 'task.activity.dealHidden' | translate }}
                      </span>
                    }
                    @if (task.isNextAction) {
                      · <span class="badge bg-soft-primary text-primary">{{ 'task.activity.nextAction' | translate }}</span>
                    }
                  </small>
                  <!-- RF-09, RF-11, RF-13: what a conclusion recorded. -->
                  @if (task.outcome) {
                    <small class="text-muted">
                      <i class="mdi mdi-flag-checkered me-1"></i>{{ outcomeLabel(task) | translate }}
                      @if (task.durationMinutes !== undefined && task.durationMinutes !== null) {
                        · {{ 'task.activity.duration' | translate: { minutes: task.durationMinutes } }}
                      }
                      @if (task.conclusionSnapshot?.stageName) {
                        · {{ 'task.activity.snapshot' | translate: { stage: task.conclusionSnapshot.stageName } }}
                      }
                    </small>
                  }
                  @if (task.outcomeNote) {
                    <small class="text-muted"><i class="mdi mdi-note-text-outline me-1"></i>{{ task.outcomeNote }}</small>
                  }
                  @if (task.nextStepSkipReason) {
                    <small class="text-muted"><i class="mdi mdi-skip-next-outline me-1"></i>{{ 'task.activity.skipped' | translate: { reason: task.nextStepSkipReason } }}</small>
                  }
                </div>
              </div>
            </td>
            <td>
              @if (momentOf(task); as moment) {
                {{ moment | timezoneDate : momentFormat(task) : getTimezoneCompanyCurrentUser() }}
                @if (task.untimed && !task.completedAt) {
                  <small class="text-muted d-block"><i class="mdi mdi-calendar-blank-outline me-1"></i>{{ 'task.activity.untimed' | translate }}</small>
                }
              } @else {
                <span class="text-muted">—</span>
              }
              <!-- RF-26: postponed N× and the moment it was first scheduled for. -->
              @if (postponementCount(task) > 0) {
                <small class="text-muted d-block">
                  <i class="mdi mdi-history me-1"></i>{{ 'task.activity.statePostponed' | translate: { count: postponementCount(task) } }}
                  @if (originalMoment(task); as original) {
                    · {{ 'task.activity.originalMoment' | translate: { moment: (original | timezoneDate : 'DD/MM HH:mm' : getTimezoneCompanyCurrentUser()) } }}
                  }
                </small>
              }
            </td>
            <td>
              @if (stateOf(task); as state) {
                <span class="badge campaign-badge" [ngClass]="state.badge" [attr.aria-label]="state.key | translate">
                  <i class="mdi me-1" [ngClass]="state.icon"></i>{{ state.key | translate }}
                </span>
              }
            </td>
            @if (showResponsible) {
              <td>{{ task.user?.name ?? '—' }}</td>
            }
            <td class="text-end">
              <button
                class="btn btn-dropdown-list"
                type="button"
                [matMenuTriggerFor]="menu"
                [attr.aria-label]="'task.activity.actions' | translate"
                (click)="$event.stopPropagation()"
                (mousedown)="$event.stopPropagation()"
                (keydown)="$event.stopPropagation()"
              >
                <i class="mdi mdi-dots-vertical"></i>
              </button>
              <mat-menu #menu="matMenu">
                @if (canConclude(task)) {
                  <button mat-menu-item type="button" (click)="conclude(task); $event.stopPropagation()">
                    <i class="mdi mdi-check-circle-outline me-1"></i>{{ 'task.activity.conclude' | translate }}
                  </button>
                }
                @if (canPostpone(task)) {
                  <button mat-menu-item type="button" (click)="postpone(task); $event.stopPropagation()">
                    <i class="mdi mdi-calendar-clock me-1"></i>{{ 'task.activity.postpone' | translate }}
                  </button>
                }
                <button mat-menu-item type="button" (click)="open(task); $event.stopPropagation()">
                  <i class="mdi mdi-open-in-new me-1"></i>{{ 'task.activity.open' | translate }}
                </button>
                @if (showDeal && idOfDeal(task)) {
                  <button mat-menu-item type="button" (click)="openDeal(task); $event.stopPropagation()">
                    <i class="mdi mdi-tag-outline me-1"></i>{{ 'task.activity.openDeal' | translate }}
                  </button>
                }
              </mat-menu>
            </td>
          </tr>
        }
      </tbody>
    </table>
  </div>
}
